// @givmo/fundraisers-module — what each email raised (EMC-WEB2), built to mockup 20 of the walkthrough Jonathan
// approved: the line "Gifts from these emails: $14,055 (109 gifts)" and the next email's, over one row per email
// that went out, with its delivery, clicks, unsubscribes, gifts and dollars. The email's own page shows the
// same figures (`EmailResultsCard`).
//
// ⚠ EVERY FIGURE IS THE SERVER'S (EMC-BE8): only what Givmo can prove. A figure the server has no record of
// is null and shows as "—", never 0: an email's delivery until the mail provider reports it, and its clicks
// and gifts when it went out before its links went through Givmo. Money is the server's decimal string,
// formatted and never added up here: the series' total is its own read (`emailResultsSummaryOptions`).

import type { ReactNode } from 'react';
import { ChevronRight, Info } from 'lucide-react';
import {
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Icon,
  Popover,
  testIdProp,
  type DataTableColumn,
} from '@givmo/design-system';
import { EMAILS_COPY as C } from './emails-copy';
import {
  emailDay,
  seriesNeedsYear,
  type EmailResults,
  type EmailResultsSummary,
  type EmailRow,
} from './emails-decode';
import { formatFundraiserMoney } from './fundraisers-decode';

type Figure = Exclude<keyof typeof C.resultsColumns, 'email'>;
const FIGURES: Figure[] = ['emailed', 'delivered', 'clicks', 'unsubscribed', 'gifts', 'raised'];

function figure(results: EmailResults, key: Figure): string {
  if (key === 'raised') return formatFundraiserMoney(results.raised);
  const value = results[key];
  return value === null ? '—' : value.toLocaleString('en-US');
}

/** A figure's label; Clicks carries its help (review M1: a mail filter's click counts too). */
function FigureLabel({ figure: key }: { figure: Figure }): ReactNode {
  if (key !== 'clicks') return C.resultsColumns[key];
  return (
    <span className="inline-flex items-center gap-2">
      {C.resultsColumns.clicks}
      <Popover.Root>
        <Popover.Trigger
          aria-label={C.clicksHelpLabel}
          className="inline-flex size-16 items-center justify-center rounded-full text-icon outline-none transition-colors hover:text-text-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
          onClick={(event) => event.stopPropagation()}
          {...testIdProp('fundraiser-email-results-clicks-help')}
        >
          <Icon icon={Info} size={14} aria-hidden />
        </Popover.Trigger>
        <Popover.Content align="end" className="w-[260px] rounded-md px-16 py-14 shadow-md">
          <span className="font-sans text-s12 normal-case leading-relaxed tracking-normal text-text-secondary">
            {C.clicksHelp}
          </span>
        </Popover.Content>
      </Popover.Root>
    </span>
  );
}

/** Whether a figure shows as "—" (the note under the figures says what a dash means only then). */
function hasUnknown(results: EmailResults): boolean {
  return FIGURES.some((key) => results[key] === null);
}

/** The rows that went out (the server answered results for them), in the series' order. */
export function emailsWithResults(rows: EmailRow[]): (EmailRow & { results: EmailResults })[] {
  return rows.filter((row): row is EmailRow & { results: EmailResults } => row.results !== null);
}

/** "To people who gave to this campaign": an audience's words after "To", its first letter lowered
 *  unless the name starts with a capitalised word ("KCC board" stays as written). */
function toAudience(words: string): string {
  // The campaign's own donors, in the user test's short words, so the line stays one line.
  if (words === C.campaignDonors) return C.toCampaignDonors;
  const lowered = /^[A-Z][a-z]/.test(words)
    ? words.charAt(0).toLowerCase() + words.slice(1)
    : words;
  return C.toAudience(lowered);
}

function EmailCell({
  row,
  year,
  audience,
}: {
  row: EmailRow & { results: EmailResults };
  year: boolean;
  /** Who it went to, named only where it isn't the series' usual audience (tester fold 2). */
  audience: string | null;
}) {
  const skipped = row.results.skippedGave;
  const parts = [
    row.scheduledFor !== null ? emailDay(row.scheduledFor, row.tz, { year }) : null,
    audience !== null ? toAudience(audience) : null,
    // It went out to no one: the row says so, as the email's own header does.
    row.noRecipients ? C.status.noOne : null,
    row.rule === 'ask' && skipped !== null && skipped > 0 ? C.skippedGave(skipped) : null,
  ].filter((part): part is string => part !== null);
  return (
    <span className="flex min-w-0 flex-col gap-2">
      <span className="font-w600 text-text-strong wrap-anywhere">{row.subject || C.noSubject}</span>
      {/* Each part stays whole, so a narrow column breaks between them, never inside one. */}
      <span className="text-s13 text-text-secondary">
        {parts.map((part, i) => (
          <span key={part}>
            {i > 0 && ' · '}
            <span className="whitespace-nowrap">{part}</span>
          </span>
        ))}
      </span>
    </span>
  );
}

/** The audience most of these emails went to (its words), or null when no words are known. */
function usualAudience(words: (string | null)[]): string | null {
  const counts = new Map<string, number>();
  for (const word of words) if (word !== null) counts.set(word, (counts.get(word) ?? 0) + 1);
  let best: string | null = null;
  for (const [word, n] of counts) if (best === null || n > (counts.get(best) ?? 0)) best = word;
  return best;
}

export function EmailResultsSection({
  rows,
  summary,
  next,
  now,
  describe,
  onOpen,
  rowActions,
}: {
  rows: EmailRow[];
  /** An email's audience in the series' words ("People who gave to this campaign"), or null. */
  describe: (row: EmailRow) => string | null;
  summary: EmailResultsSummary | null | undefined;
  /** The next email still to go out, "Next: “Meet Morgan” on Fri, Dec 18". */
  next: EmailRow | null;
  now: Date;
  onOpen: (row: EmailRow) => void;
  /** A row's own acts, beside its chevron (the series' Duplicate), for the table or the narrow card. */
  rowActions?: (row: EmailRow, place: 'email' | 'email-card') => ReactNode;
}) {
  const wentOut = emailsWithResults(rows);
  if (wentOut.length === 0) return null;
  // Mockup 20 draws no year: a day carries one only when these rows (or the next) cross into another.
  const year = seriesNeedsYear(wentOut, now);
  const nextYear = next !== null && seriesNeedsYear([next], now);
  const usual = usualAudience(wentOut.map(describe));
  const audienceOf = (row: EmailRow) => {
    const words = describe(row);
    return words !== null && words !== usual ? words : null;
  };
  const columns: DataTableColumn<EmailRow & { results: EmailResults }>[] = [
    {
      id: 'email',
      header: C.resultsColumns.email,
      cell: (row) => <EmailCell row={row} year={year} audience={audienceOf(row)} />,
    },
    ...FIGURES.map((key) => ({
      id: key,
      header: <FigureLabel figure={key} />,
      numeric: true,
      align: 'right' as const,
      cell: (row: EmailRow & { results: EmailResults }) => (
        <span
          className={key === 'raised' ? 'font-w600 text-text-strong' : 'text-text-strong'}
          {...testIdProp(`fundraiser-email-result-${key}-${row.id}`)}
        >
          {figure(row.results, key)}
        </span>
      ),
    })),
    {
      // Tester fold 3: a row that went out opens its email, as a Coming up row does.
      id: 'open',
      header: <span className="sr-only">{C.columns.open}</span>,
      cell: (row) => (
        <span className="flex items-center justify-end gap-4">
          {rowActions?.(row, 'email')}
          <ChevronRight aria-hidden className="size-16 text-text-tertiary" />
        </span>
      ),
    },
  ];
  const totals =
    summary == null || summary.gifts === null || summary.raised === null
      ? C.totalsUnknown
      : `${formatFundraiserMoney(summary.raised)} (${C.giftCount(summary.gifts)})`;
  return (
    <section className="flex min-w-0 flex-col gap-12" {...testIdProp('fundraiser-email-results')}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-16 gap-y-4">
        <h2
          className="font-sans text-s18 font-w600 text-text-strong"
          {...testIdProp('fundraiser-email-results-total')}
        >
          {C.giftsFromEmails} {totals}
        </h2>
        {next !== null && next.scheduledFor !== null && (
          <p
            className="text-s14 text-text-secondary"
            {...testIdProp('fundraiser-email-results-next')}
          >
            {C.next(
              next.subject || C.noSubject,
              emailDay(next.scheduledFor, next.tz, { year: nextYear }),
            )}
          </p>
        )}
      </div>
      <div className="hidden lg:block [&_tbody_tr]:cursor-pointer">
        <DataTable
          testId="fundraiser-email-results-table"
          columns={columns}
          rows={wentOut}
          rowKey={(row) => row.id}
          rowTestId={(row) => `fundraiser-email-result-row-${row.id}`}
          onRowClick={onOpen}
        />
      </div>
      <div
        className="flex flex-col gap-12 lg:hidden"
        {...testIdProp('fundraiser-email-result-cards')}
      >
        {wentOut.map((row) => (
          <Card
            key={row.id}
            flush
            className="relative"
            testId={`fundraiser-email-result-card-${row.id}`}
          >
            {rowActions !== undefined && (
              <span className="absolute bottom-10 right-10 z-10">
                {rowActions(row, 'email-card')}
              </span>
            )}
            <button
              type="button"
              className="w-full rounded-lg text-left focus-visible:outline-2 focus-visible:outline-brand-500"
              onClick={() => onOpen(row)}
            >
              <CardBody className="flex flex-col gap-12 p-16">
                <span className="flex items-start justify-between gap-12">
                  <EmailCell row={row} year={year} audience={audienceOf(row)} />
                  <ChevronRight aria-hidden className="size-16 shrink-0 text-text-tertiary" />
                </span>
                <Figures results={row.results} />
              </CardBody>
            </button>
          </Card>
        ))}
      </div>
      {wentOut.some((row) => hasUnknown(row.results)) && (
        <p
          className="text-s13 text-text-secondary"
          {...testIdProp('fundraiser-email-results-note')}
        >
          {C.resultsNote}
        </p>
      )}
    </section>
  );
}

/** ``help``: the Clicks label carries its help, except where the figures sit inside a button (a
 *  narrow screen's row card), which can't hold another. */
function Figures({ results, help = false }: { results: EmailResults; help?: boolean }): ReactNode {
  return (
    <dl className="grid grid-cols-3 gap-x-12 gap-y-10">
      {FIGURES.map((key) => (
        <div key={key} className="flex min-w-0 flex-col gap-2">
          <dt className="text-s12 text-text-secondary">
            {help ? <FigureLabel figure={key} /> : C.resultsColumns[key]}
          </dt>
          <dd className="text-s16 font-w600 tabular-nums text-text-strong">
            {figure(results, key)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** An email's own page: the same figures as its row in the results (mockup 20's), once it went out. */
export function EmailResultsCard({ row }: { row: EmailRow }) {
  if (row.results === null) return null;
  const skipped = row.results.skippedGave;
  return (
    <Card flush testId="fundraiser-email-results-card">
      <CardHeader title={C.resultsTitle} />
      <CardBody className="flex flex-col gap-12">
        <Figures results={row.results} help />
        {row.rule === 'ask' && skipped !== null && skipped > 0 && (
          <p className="text-s13 text-text-secondary">{C.skippedGave(skipped)}</p>
        )}
        {hasUnknown(row.results) && <p className="text-s12 text-text-secondary">{C.resultsNote}</p>}
      </CardBody>
    </Card>
  );
}
